// Department names are stored as a stable `code` on Employee.departement
// and on every snapshot field (ExpenseReport, CalendarEvent...). Display
// labels per locale live in a small runtime catalogue fetched once from
// /api/departments/ at app startup (DepartmentsBootstrap in Shell).
//
// Until that fetch completes we fall back to the hardcoded translations
// below · matches what the seed migration starts with.
//
// Admin renames update the catalogue in place, which notifies subscribers
// so live components (admin tab, calendar pills) reflect the new label.

import { useCallback, useEffect, useState } from 'react';

const STATIC_TRANSLATIONS = {
  'Direction Générale':         { fr: 'Direction Générale',         nl: 'Algemene Directie',       en: 'General Management' },
  'Ressources Humaines':        { fr: 'Ressources Humaines',        nl: 'Personeelszaken',          en: 'Human Resources' },
  'IT & Systèmes':              { fr: 'IT & Systèmes',              nl: 'IT & Systemen',            en: 'IT & Systems' },
  'Marketing & Communication':  { fr: 'Marketing & Communication',  nl: 'Marketing & Communicatie', en: 'Marketing & Communications' },
  'Service Client':             { fr: 'Service Client',             nl: 'Klantendienst',            en: 'Customer Service' },
  'Commercial & Ventes':        { fr: 'Commercial & Ventes',        nl: 'Commercieel & Verkoop',    en: 'Sales' },
  'Finance & Comptabilité':     { fr: 'Finance & Comptabilité',     nl: 'Financiën & Boekhouding',  en: 'Finance & Accounting' },
  'Logistique':                 { fr: 'Logistique',                 nl: 'Logistiek',                en: 'Logistics' },
  'Calum':                      { fr: 'Calum',                      nl: 'Calum',                    en: 'Calum' },
};

let _catalogue = null;  // Array<{id, code, name_fr, name_nl, name_en, sort_order}> | null
const _subs = new Set();

export function setDepartmentCatalogue(items) {
  _catalogue = Array.isArray(items) ? items.slice() : [];
  _subs.forEach(fn => { try { fn(); } catch { /* ignore */ } });
}

export function deptLabel(code, locale) {
  if (!code) return '';
  if (_catalogue) {
    const d = _catalogue.find(x => x.code === code);
    if (d) {
      const v = ({ fr: d.name_fr, nl: d.name_nl, en: d.name_en })[locale];
      return v || d.name_fr || code;
    }
  }
  const t = STATIC_TRANSLATIONS[code];
  return t?.[locale] || t?.fr || code;
}

export function getDepartments() {
  if (_catalogue) return _catalogue;
  return Object.entries(STATIC_TRANSLATIONS).map(([code, t], i) => ({
    id: -i - 1, code, name_fr: t.fr, name_nl: t.nl, name_en: t.en, sort_order: i,
  }));
}

export function getDepartmentCodes() {
  return getDepartments().map(d => d.code);
}

/**
 * React hook · returns the live departments catalogue + the deptLabel
 * helper, and re-renders the caller when the catalogue changes (admin
 * adds / renames / removes a dept).
 */
export function useDepartments() {
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick(v => v + 1), []);
  useEffect(() => {
    _subs.add(bump);
    return () => _subs.delete(bump);
  }, [bump]);
  return {
    departments: getDepartments(),
    deptLabel,
  };
}

// --- Legacy export · kept so existing imports of DEPARTMENTS keep working.
// New code should use getDepartmentCodes() so runtime additions are reflected.
export const DEPARTMENTS = Object.keys(STATIC_TRANSLATIONS);
